import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from '@ifixit/ui/components/icon';
import { toast } from '@ifixit/ui/components/toast';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import type { ReactNode } from 'react';
import {
   copyImageWithToast,
   type LegacyImageInput,
   openImageCrop,
} from 'Shared/editor/legacy_image_dialogs';

/**
 * A document's primary photo (guide_meta.imageid, wikis.summary_imageid),
 * shown large and replaceable through the Media Manager. The pick bar stays
 * visible while no image is set and on coarse pointers (no hover to reveal
 * it); over a picked image on fine pointers it reveals on hover/focus. When
 * no image is set, `fallbackUrl` stands in (the guide editor shows the
 * device photo, matching the guide view's fallback), and a placeholder box
 * renders when neither exists.
 *
 * Both primary-image collections require a 4:3 ratio and a minimum size.
 * Hard failures gray out in the picker, but an off-ratio image comes back
 * with a 'crop' verdict this field honors: it routes through the crop
 * dialog — which enforces the ratio — and attaches the cropped copy, like
 * v1's media targets. Canceling attaches nothing.
 */
export function PrimaryImageField({
   image,
   onChange,
   accepts,
   fallbackUrl = null,
   ariaLabel,
   caption,
   setLabel,
   onProcessingChange,
   className,
}: {
   image: { imageid: number; url: string | null } | null;
   onChange: (image: { imageid: number; url: string | null } | null) => void;
   /** The Media Manager collection to pick from, e.g. 'guide_main_image'. */
   accepts: string;
   /** Shown when no image is set; omit to show the placeholder box. */
   fallbackUrl?: string | null;
   ariaLabel: string;
   /**
    * Names the source of what's shown above the preview — the document's
    * own image vs. the fallback standing in. Omit when the surrounding
    * card already titles the field.
    */
   caption?: { image: string; fallback: string };
   /** The pick button's label when no image is set yet. */
   setLabel: string;
   /** Reports background crop work so the document cannot save the old image. */
   onProcessingChange?: (isProcessing: boolean) => void;
   className?: string;
}) {
   // A URL that 404s renders as a zero-height <img>, silently emptying the
   // column the layout reserves for it; fall back to the placeholder box.
   // The caller keys this component by image id, so switching images resets
   // the flag.
   const [failed, setFailed] = useState(false);
   // True while a cropped image is being produced in the background, after
   // the crop dialog closes but before the image is ready to place.
   const [processing, setProcessing] = useState(false);
   // Guards the media-manager pick: `import('Shared/MediaManager')` code-splits,
   // so the first click sits on a cold import with nothing on screen yet — long
   // enough to click again and open two pickers.
   const [isPicking, setIsPicking] = useState(false);
   const url = image?.url ?? fallbackUrl;

   // Runs a picked or existing image through the crop dialog and adopts the
   // cropped copy. The dialog enforces the collection's ratio; `onChange`
   // gets the new imageid its background job mints, and the page stays
   // blocked (processing) until that state lands so it can't save the old
   // image. Canceling changes nothing.
   const runCrop = async (input: LegacyImageInput) => {
      let cropped = null;
      try {
         cropped = await openImageCrop(input, accepts, () => {
            setProcessing(true);
            onProcessingChange?.(true);
         });
      } catch {
         toast.error(_js('The image editor did not open. Try again.'), { duration: 5000 });
         onProcessingChange?.(false);
         return;
      } finally {
         setProcessing(false);
      }
      try {
         if (cropped) {
            onChange({ imageid: cropped.imageid, url: imageRenditionUrl(cropped) });
         }
      } finally {
         onProcessingChange?.(false);
      }
   };

   const pickImage = async () => {
      if (isPicking) {
         return;
      }
      setIsPicking(true);
      try {
         await runPick();
      } finally {
         setIsPicking(false);
      }
   };

   const runPick = async () => {
      const { openMediaManager } = await import('Shared/MediaManager');
      const item = await openMediaManager({ accepts });
      if (!item || item.imageid === undefined) {
         return;
      }
      if (item.filter_state !== 'crop') {
         onChange({ imageid: item.imageid, url: imageRenditionUrl(item) });
         return;
      }
      await runCrop({
         imageid: item.imageid,
         thumbnail: item.thumbnail ?? null,
         standard: item.standard ?? null,
         medium: item.medium ?? null,
         original: item.original ?? null,
      });
   };

   // Re-crop the image already on the document. The crop dialog reloads the
   // full-resolution source from the image server by id; the url only seeds
   // its preview canvas, so passing the one rendition this field holds is
   // enough.
   const cropCurrentImage = () => {
      if (image) {
         void runCrop({ imageid: image.imageid, medium: image.url });
      }
   };

   return (
      <div role="group" aria-label={ariaLabel} className={cn('group', className)}>
         {caption && (
            // Name the fallback only while it actually shows.
            <span className="mb-1 block text-sm font-medium text-gray-950">
               {!image && url && !failed ? caption.fallback : caption.image}
            </span>
         )}
         {/* A 4:3 box — the ratio both primary-image collections enforce —
             so a placed image fills it edge to edge and the field reserves
             the same shape before the bits arrive (a non-4:3 fallback still
             letterboxes inside via object-contain). */}
         {url && !failed ? (
            <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg border border-gray-300 bg-white">
               <img
                  src={url}
                  alt=""
                  onError={() => setFailed(true)}
                  className="max-h-full max-w-full object-contain"
               />
               {processing && <ProcessingOverlay />}
               <div
                  className={cn(
                     // /60, not /50: white text over a 50% scrim on a
                     // light photo measures 3.98:1, under the 4.5:1 this
                     // 14px label needs.
                     'absolute inset-x-0 bottom-0 flex justify-center bg-black/60 p-2',
                     // Only cover a picked image on demand; the fallback keeps
                     // its one action visible, and coarse pointers have no
                     // hover to reveal it with.
                     image &&
                        'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100'
                  )}
               >
                  <button
                     type="button"
                     onClick={() => void pickImage()}
                     disabled={isPicking || processing}
                     className="rounded-md px-3 py-1 text-sm font-medium text-white outline-none hover:bg-black/40 focus-visible:ring-[3px] focus-visible:ring-white/80 disabled:pointer-events-none disabled:opacity-50"
                  >
                     {image ? _js('Replace image') : setLabel}
                  </button>
               </div>
               {image && (
                  <ImageActionsMenu
                     onCrop={cropCurrentImage}
                     onCopy={() => void copyImageWithToast(image.imageid)}
                     onRemove={() => onChange(null)}
                     // The background crop holds the old image until its new id
                     // lands; letting Delete/Crop run against it now would race
                     // that write (a Delete the pending crop then resurrects).
                     disabled={processing}
                     className="absolute right-2 top-2 opacity-0 transition-opacity focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
                  />
               )}
            </div>
         ) : (
            <MediaDropZone label={setLabel} onPick={() => void pickImage()}>
               {processing && <ProcessingOverlay />}
            </MediaDropZone>
         )}
      </div>
   );
}

/**
 * The empty state of a media field: a dashed full-width drop-zone box with
 * a single button that opens the Media Manager (which handles uploads and
 * drag-and-drop itself).
 */
function MediaDropZone({
   label,
   onPick,
   children,
}: {
   label: string;
   onPick: () => void;
   children?: ReactNode;
}) {
   return (
      <div className="relative flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
         <Button type="button" className="button-outline button-md" onClick={onPick}>
            {label}
         </Button>
         {children}
      </div>
   );
}

/** The background-crop veil over either state of the preview. */
function ProcessingOverlay() {
   return (
      <span
         role="status"
         className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-white/70"
      >
         <FaIcon
            name="spinner"
            className="size-8 animate-spin text-gray-500 motion-reduce:animate-none"
         />
         {/* The spinner carries the message for sighted users; under
             reduced motion a static spinner reads as stuck, so the
             text becomes visible. */}
         <span className="sr-only text-sm font-medium text-gray-700 motion-reduce:not-sr-only">
            {_js('Preparing image…')}
         </span>
      </span>
   );
}

/**
 * The corner "⋯" menu on a placed primary image: Crop (re-crop in place),
 * Copy to media library, and Delete. Shares the circular dark chrome and
 * caller-driven hover/focus reveal with {@link RemoveMediaButton}.
 */
function ImageActionsMenu({
   onCrop,
   onCopy,
   onRemove,
   disabled,
   className,
}: {
   onCrop: () => void;
   onCopy: () => void;
   onRemove: () => void;
   disabled?: boolean;
   className?: string;
}) {
   return (
      <DropdownMenu>
         <DropdownMenuTrigger asChild>
            <button
               type="button"
               aria-label={_js('Image actions')}
               disabled={disabled}
               // size-7 plus the coarse-pointer inset matches the step editor's
               // image menu at a 44px touch target; the hit area belongs to the
               // control, not to wherever a caller happens to place it.
               className={cn(
                  "flex size-7 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white/80 disabled:pointer-events-none disabled:opacity-50 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']",
                  className
               )}
            >
               <FaIcon name="ellipsis" className="size-4" />
            </button>
         </DropdownMenuTrigger>
         <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onCrop}>
               <FaIcon name="crop-simple" className="size-3.5" />
               {_js('Crop')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onCopy}>
               <FaIcon name="copy" className="size-3.5" />
               {_js('Copy to media library')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onRemove}>
               <FaIcon name="trash-can" className="size-3.5" />
               {_js('Delete')}
            </DropdownMenuItem>
         </DropdownMenuContent>
      </DropdownMenu>
   );
}

/**
 * The circular × that clears a picked media item (image or video). Position
 * and hover/focus-reveal are the caller's via className; the button styling
 * and the icon are shared.
 */
export function RemoveMediaButton({
   label,
   onClick,
   className,
}: {
   label: string;
   onClick: () => void;
   className?: string;
}) {
   return (
      <button
         type="button"
         aria-label={label}
         title={label}
         onClick={onClick}
         className={cn(
            "flex size-7 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white/80 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']",
            className
         )}
      >
         <FaIcon name="trash-can" className="size-3.5" />
      </button>
   );
}

/**
 * The display URL for a picked image, preferring a mid-size rendition and
 * falling back to the original. Shared by the initial seed (an ApiImage from
 * the document GET) and the Media Manager pick (a MediaLibraryItem) so both
 * resolve the same rendition — and so the display-only url never drives the
 * imageid-based dirty check.
 */
export function imageRenditionUrl(sizes: {
   medium?: string | null;
   standard?: string | null;
   large?: string | null;
   original?: string | null;
}): string | null {
   return sizes.medium ?? sizes.standard ?? sizes.large ?? sizes.original ?? null;
}
